import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { AgentMarketPlaceItemWithFilter } from '../../pages/userAgent/agentMarketPlace/AgentMarketPlace';

interface AgentAddModalProps {
  open: boolean;
  newAgentData: Partial<AgentMarketPlaceItemWithFilter & { folder_name?: string; party_id?: string }>;
  isFileUploadMode: boolean;
  uploadedFile: File | null;
  fileUploadError: string;
  deploymentSuccess: boolean;
  isDeployingAgent: boolean;
  fileUploadSuccess: boolean;
  onChange: (field: keyof AgentMarketPlaceItemWithFilter, value: any) => void;
  onSave: () => void;
  onCancel: () => void;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onToggleUploadMode: (checked: boolean) => void;
}

const AgentAddModal: React.FC<AgentAddModalProps> = ({
  open,
  newAgentData,
  isFileUploadMode,
  uploadedFile,
  fileUploadError,
  deploymentSuccess,
  isDeployingAgent,
  fileUploadSuccess,
  onChange,
  onSave,
  onCancel,
  onFileUpload,
  onToggleUploadMode,
}) => {
  const [showValidation, setShowValidation] = React.useState(false);
  const [ownersInput, setOwnersInput] = React.useState('');
  const [lastSyncedOwners, setLastSyncedOwners] = React.useState<any>(null);

  // Reset validation state when modal is opened
  React.useEffect(() => {
    if (open) {
      setShowValidation(false);
    }
  }, [open]);

  // Sync local state with newAgentData.owners when it changes externally
  React.useEffect(() => {
    const ownersChanged = JSON.stringify(newAgentData.owners) !== JSON.stringify(lastSyncedOwners);
    
    if (ownersChanged) {
      if (newAgentData.owners && Array.isArray(newAgentData.owners) && newAgentData.owners.length > 0) {
        const formattedOwners = newAgentData.owners
          .map((o: any) => `${o.name || ''}:${o.email || ''}`)
          .join(', ');
        setOwnersInput(formattedOwners);
      } else {
        setOwnersInput('');
      }
      setLastSyncedOwners(newAgentData.owners);
    }
  }, [newAgentData.owners, lastSyncedOwners]);

  const handleOwnersChange = (value: string) => {
    setOwnersInput(value);
    
    // Parse the input into owners array
    const parsed = value.split(',').map((str: string) => {
      const trimmed = str.trim();
      const colonIndex = trimmed.indexOf(':');
      if (colonIndex === -1) {
        return { name: trimmed, email: '' };
      }
      const name = trimmed.substring(0, colonIndex).trim();
      const email = trimmed.substring(colonIndex + 1).trim();
      return { name, email };
    });
    
    onChange('owners', parsed);
    setLastSyncedOwners(parsed);
  };

  // Close modal on successful deployment
  React.useEffect(() => {
    if (deploymentSuccess) {
      const timer = setTimeout(() => {
        onCancel();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [deploymentSuccess, onCancel]);

  // Validation logic for required fields (as per user request)
  const validationErrors = {
    name: !newAgentData.name || newAgentData.name.trim() === '',
    party_type: !newAgentData.party_type || newAgentData.party_type.trim() === '',
    description: !newAgentData.description || newAgentData.description.trim() === '',
    tech_stack: !newAgentData.tech_stack || (Array.isArray(newAgentData.tech_stack) && newAgentData.tech_stack.length === 0) || (typeof newAgentData.tech_stack === 'string' && (newAgentData.tech_stack as string).trim() === ''),
    category: !newAgentData.category || newAgentData.category.trim() === '',
    role: !newAgentData.role || newAgentData.role.trim() === '',
    cloud_provider: !newAgentData.cloud_provider || newAgentData.cloud_provider.trim() === '',
    owners: !newAgentData.owners || (Array.isArray(newAgentData.owners) && newAgentData.owners.length === 0) || (Array.isArray(newAgentData.owners) && newAgentData.owners.some((o: any) => !o.name || !o.email || o.name.trim() === '' || o.email.trim() === '')),
    last_updated: !newAgentData.last_updated || newAgentData.last_updated.trim() === '',
    folder_name: !newAgentData.folder_name || newAgentData.folder_name.trim() === '',
    downloadable_only: typeof newAgentData.downloadable_only !== 'boolean',
    service_name: !newAgentData.service_name || newAgentData.service_name.trim() === '',
    supports_file_upload: typeof newAgentData.supports_file_upload !== 'boolean',
    prompt1: !newAgentData.prompt1 || newAgentData.prompt1.trim() === '',
    prompt2: !newAgentData.prompt2 || newAgentData.prompt2.trim() === '',
    prompt3: !newAgentData.prompt3 || newAgentData.prompt3.trim() === '',
    custom_ui: false,
    file_format_supported: newAgentData.supports_file_upload === true && (!newAgentData.file_format_supported || (Array.isArray(newAgentData.file_format_supported) && newAgentData.file_format_supported.length === 0) || (typeof newAgentData.file_format_supported === 'string' && (newAgentData.file_format_supported as string).trim() === '')),
    detailed_description: !newAgentData.detailed_description || newAgentData.detailed_description.trim() === '',
  };

  // Check if there are any validation errors
  const hasValidationErrors = Object.values(validationErrors).some(error => error === true);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>Add Agent</DialogTitle>
      <DialogContent dividers>
        {/* Deployment Loader Overlay */}
        {isDeployingAgent && (
          <Backdrop
            sx={{
              position: 'absolute',
              zIndex: (theme) => theme.zIndex.drawer + 1,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: 1,
            }}
            open={isDeployingAgent}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2, color: 'primary.main' }}>
                Deploying Agent...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please wait while we deploy your agent
              </Typography>
            </Box>
          </Backdrop>
        )}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {isFileUploadMode ? 'Upload Agent Configuration File' : 'Fill Agent Details Manually'}
          </Typography>
          <FormControlLabel
            control={<Switch checked={isFileUploadMode} onChange={e => onToggleUploadMode(e.target.checked)} color="primary" />}
            label={isFileUploadMode ? 'File Upload' : 'Manual Entry'}
            labelPlacement="start"
          />
        </Box>
        <Divider sx={{ mb: 3 }} />
        {isFileUploadMode ? (
          <Box>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <input
                accept=".zip"
                style={{ display: 'none' }}
                id="agent-file-upload"
                type="file"
                onChange={onFileUpload}
                disabled={isDeployingAgent}
              />
              <label htmlFor="agent-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  disabled={isDeployingAgent}
                  sx={{ minHeight: 60, minWidth: 200, borderStyle: 'dashed', borderWidth: 2 }}
                >
                  {isDeployingAgent ? (
                    'Deploying...'
                  ) : uploadedFile ? (
                    uploadedFile.name
                  ) : (
                    'Choose ZIP Folder to Upload'
                  )}
                </Button>
              </label>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Supported format: ZIP folders only
              </Typography>
              {fileUploadError && (
                <Alert severity="error" sx={{ mt: 2, maxWidth: 400, mx: 'auto' }}>{fileUploadError}</Alert>
              )}
              {fileUploadSuccess && (
                <Alert severity="success" sx={{ mt: 2, maxWidth: 400, mx: 'auto' }}>
                  ZIP file uploaded successfully! Please click "Deploy Agent" to deploy.
                </Alert>
              )}
              {deploymentSuccess && (
                <Alert severity="success" sx={{ mt: 2, maxWidth: 400, mx: 'auto' }}>
                  Agent deployed successfully! The agent has been saved and deployed.
                </Alert>
              )}
            </Box>
          </Box>
        ) : (
          <>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              label="Party ID"
              value={newAgentData._id || ''}
              onChange={e => onChange('_id', e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              required
              error={showValidation && !!newAgentData._id && !/^([0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12})$/.test(newAgentData._id as string)}
              helperText={showValidation ? (
                !newAgentData._id
                  ? 'Auto-generated if left empty'
                  : !/^([0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12})$/.test(newAgentData._id as string)
                    ? 'Party ID must be a valid UUID (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)'
                    : 'Valid UUID'
              ) : ''}
            />
            <TextField label="Name" value={newAgentData.name || ''} onChange={e => onChange('name', e.target.value)} fullWidth sx={{ mb: 2 }} required error={showValidation && validationErrors.name} helperText={showValidation && validationErrors.name ? 'Name is required and cannot be empty' : ''} />
            <FormControl fullWidth sx={{ mb: 2 }} required>
              <InputLabel id="party-type-label">Party Type</InputLabel>
              <Select
                labelId="party-type-label"
                value={newAgentData.party_type || ''}
                label="Party Type"
                onChange={e => onChange('party_type', e.target.value)}
                error={showValidation && validationErrors.party_type}
              >
                <MenuItem value="Agent">Agent</MenuItem>
                <MenuItem value="Team">Team</MenuItem>
              </Select>
              {showValidation && validationErrors.party_type && (
                <Box sx={{ color: 'error.main', fontSize: '0.8rem', mt: 0.5, ml: 2 }}>Party Type is required and cannot be empty</Box>
              )}
            </FormControl>
            <TextField label="Description" value={newAgentData.description || ''} onChange={e => onChange('description', e.target.value)} fullWidth sx={{ mb: 2 }} required error={showValidation && validationErrors.description} helperText={showValidation && validationErrors.description ? 'Description is required and cannot be empty' : ''} />
            <TextField
              label="Tech Stack (comma separated)"
              value={typeof newAgentData.tech_stack === 'string'
                ? newAgentData.tech_stack
                : Array.isArray(newAgentData.tech_stack)
                  ? newAgentData.tech_stack.join(', ')
                  : ''}
              onChange={e => onChange('tech_stack', e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              required
              placeholder="e.g. python, node, javascript"
              error={showValidation && validationErrors.tech_stack}
              helperText={showValidation && validationErrors.tech_stack ? 'Tech Stack is required and cannot be empty' : ''}
            />
            <TextField label="Category" value={newAgentData.category || ''} onChange={e => onChange('category', e.target.value)} fullWidth sx={{ mb: 2 }} required error={showValidation && validationErrors.category} helperText={showValidation && validationErrors.category ? 'Category is required and cannot be empty' : ''} />
            <TextField label="Role" value={newAgentData.role || ''} onChange={e => onChange('role', e.target.value)} fullWidth sx={{ mb: 2 }} required error={showValidation && validationErrors.role} helperText={showValidation && validationErrors.role ? 'Role is required and cannot be empty' : ''} />
            {newAgentData.party_type === 'Team' && (
              <TextField
                label="Sub Agents"
                type="number"
                value={(newAgentData as any).agents || ''}
                onChange={e => onChange('agents' as any, parseInt(e.target.value) || '')}
                fullWidth
                sx={{ mb: 2 }}
                helperText="Optional - Enter sub agent ID (integer only), will be sent as null if empty"
              />
            )}
            <TextField label="Cloud Provider" value={newAgentData.cloud_provider || ''} onChange={e => onChange('cloud_provider', e.target.value)} fullWidth sx={{ mb: 2 }} required error={showValidation && validationErrors.cloud_provider} helperText={showValidation && validationErrors.cloud_provider ? 'Cloud Provider is required and cannot be empty' : ''} />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel shrink id="all-only-label">Allow All Access</InputLabel>
              <Select
                labelId="all-only-label"
                value="true"
                label="Allow All Access"
                disabled
              >
                <MenuItem value="true">True</MenuItem>
              </Select>
            </FormControl>
            <TextField 
              label="Owners (name:email, ...)" 
              value={ownersInput} 
              onChange={e => handleOwnersChange(e.target.value)} 
              fullWidth 
              sx={{ mb: 2 }} 
              required 
              error={showValidation && validationErrors.owners} 
              helperText={showValidation && validationErrors.owners ? 'At least one owner is required and each must have both name and email' : 'Format: Name:email@example.com, Name2:email2@example.com'} 
            />
            <TextField
              label="Last Updated"
              type="date"
              value={newAgentData.last_updated ? newAgentData.last_updated.slice(0, 10) : ''}
              onChange={e => onChange('last_updated', e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              required
              InputLabelProps={{ shrink: true }}
              error={showValidation && validationErrors.last_updated}
              helperText={showValidation && validationErrors.last_updated ? 'Last Updated is required and cannot be empty' : ''}
            />
            <TextField label="Folder Name" value={newAgentData.folder_name || ''} onChange={e => onChange('folder_name', (e.target.value as string).replace(/[^a-z0-9_]/g, '_').toLowerCase())} fullWidth sx={{ mb: 2 }} required error={showValidation && validationErrors.folder_name} helperText={showValidation && validationErrors.folder_name ? 'Folder Name is required and cannot be empty' : 'This will be used as the ZIP folder name (lowercase, underscores allowed)'} />
            {/* Icon field removed. 'icon' will be set to 'Cloud' on save/download. */}
            <FormControl fullWidth sx={{ mb: 2 }} required error={showValidation && validationErrors.downloadable_only}>
              <InputLabel id="downloadable-only-label">Downloadable only</InputLabel>
              <Select
                labelId="downloadable-only-label"
                value={newAgentData.downloadable_only === true ? 'true' : newAgentData.downloadable_only === false ? 'false' : ''}
                label="Downloadable only"
                onChange={e => {
                  const isDownloadableOnly = e.target.value === 'true';
                  onChange('downloadable_only', isDownloadableOnly);
                  // If downloadable_only is false and supports_dataset is not 'existing', auto-select external for custom_ui
                  if (!isDownloadableOnly && newAgentData.supports_dataset !== 'existing') {
                    onChange('custom_ui', 'external');
                  }
                }}
              >
                <MenuItem value="true">True</MenuItem>
                <MenuItem value="false">False</MenuItem>
              </Select>
              {showValidation && validationErrors.downloadable_only && (
                <Box sx={{ color: 'error.main', fontSize: '0.8rem', mt: 0.5, ml: 2 }}>Please select Downloadable only (required)</Box>
              )}
            </FormControl>
            {/* Is Platform Agent field removed. Always send isPlatformAgent as false on save/download. */}
            <TextField label="Service Name" value={newAgentData.service_name || ''} onChange={e => onChange('service_name', e.target.value)} fullWidth sx={{ mb: 2 }} required error={showValidation && validationErrors.service_name} helperText={showValidation && validationErrors.service_name ? 'Service Name is required and cannot be empty' : ''} />
            <TextField label="Port" type="number" value={newAgentData.port ?? ''} onChange={e => onChange('port', e.target.value === '' ? 8000 : Number(e.target.value))} fullWidth sx={{ mb: 2 }} helperText="Optional - Default value 8000 will be sent if empty" />
            <FormControl fullWidth sx={{ mb: 2 }} required error={showValidation && validationErrors.supports_file_upload}>
              <InputLabel id="supports-file-upload-label">Supports File Upload</InputLabel>
              <Select labelId="supports-file-upload-label" value={newAgentData.supports_file_upload === true ? 'true' : newAgentData.supports_file_upload === false ? 'false' : ''} label="Supports File Upload" onChange={e => onChange('supports_file_upload', e.target.value === 'true')}>
                <MenuItem value="true">True</MenuItem>
                <MenuItem value="false">False</MenuItem>
              </Select>
              {showValidation && validationErrors.supports_file_upload && (
                <Box sx={{ color: 'error.main', fontSize: '0.8rem', mt: 0.5, ml: 2 }}>Please select Supports File Upload (required)</Box>
              )}
            </FormControl>
            {newAgentData.supports_file_upload === true && (
              <TextField label="File Format Supported (comma separated)" value={newAgentData.file_format_supported ? (Array.isArray(newAgentData.file_format_supported) ? newAgentData.file_format_supported.join(', ') : newAgentData.file_format_supported) : ''} onChange={e => onChange('file_format_supported', e.target.value.split(',').map((f: string) => f.trim()))} fullWidth sx={{ mb: 2 }} required error={!newAgentData.file_format_supported || (Array.isArray(newAgentData.file_format_supported) && newAgentData.file_format_supported.length === 0) || (typeof newAgentData.file_format_supported === 'string' && (newAgentData.file_format_supported as string).trim() === '')} helperText={!newAgentData.file_format_supported || (Array.isArray(newAgentData.file_format_supported) && newAgentData.file_format_supported.length === 0) || (typeof newAgentData.file_format_supported === 'string' && (newAgentData.file_format_supported as string).trim() === '') ? 'File Format Supported is required and cannot be empty' : ''} />
            )}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="supports-dataset-label">Supports Dataset</InputLabel>
              <Select
                labelId="supports-dataset-label"
                value={newAgentData.supports_dataset || ''}
                label="Supports Dataset"
                onChange={e => {
                  onChange('supports_dataset', e.target.value);
                  if (e.target.value === 'not used') {
                    onChange('datasetIds', '');
                  } else if (e.target.value === 'existing') {
                    // Auto-select 'not used' for custom UI when dataset is existing
                    onChange('custom_ui', 'not used');
                    // Set datasetIds structure for existing
                    onChange('datasetIds', {
                      dev: '',
                      staging: '',
                      prod: ''
                    });
                  }
                }}
              >
                <MenuItem value="existing">Existing</MenuItem>
                <MenuItem value="not used">Not Used</MenuItem>
              </Select>
            </FormControl>
            {newAgentData.supports_dataset === 'existing' && (
              <>
                <TextField
                  label="Dataset ID - Dev"
                  value={typeof newAgentData.datasetIds === 'object' && newAgentData.datasetIds ? (newAgentData.datasetIds as any).dev || '' : ''}
                  onChange={e => onChange('datasetIds', {
                    ...((typeof newAgentData.datasetIds === 'object' && newAgentData.datasetIds) ? newAgentData.datasetIds : { dev: '', staging: '', prod: '' }),
                    dev: e.target.value
                  })}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Dataset ID - Staging"
                  value={typeof newAgentData.datasetIds === 'object' && newAgentData.datasetIds ? (newAgentData.datasetIds as any).staging || '' : ''}
                  onChange={e => onChange('datasetIds', {
                    ...((typeof newAgentData.datasetIds === 'object' && newAgentData.datasetIds) ? newAgentData.datasetIds : { dev: '', staging: '', prod: '' }),
                    staging: e.target.value
                  })}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Dataset ID - Prod"
                  value={typeof newAgentData.datasetIds === 'object' && newAgentData.datasetIds ? (newAgentData.datasetIds as any).prod || '' : ''}
                  onChange={e => onChange('datasetIds', {
                    ...((typeof newAgentData.datasetIds === 'object' && newAgentData.datasetIds) ? newAgentData.datasetIds : { dev: '', staging: '', prod: '' }),
                    prod: e.target.value
                  })}
                  fullWidth
                  sx={{ mb: 2 }}
                />
              </>
            )}
            {newAgentData.supports_dataset === 'new' && (
              <TextField label="Dataset ID" value={typeof newAgentData.datasetIds === 'string' ? newAgentData.datasetIds : ''} onChange={e => onChange('datasetIds', e.target.value)} fullWidth sx={{ mb: 2 }} />
            )}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="custom-ui-label">Custom UI</InputLabel>
              <Select
                labelId="custom-ui-label"
                value={newAgentData.supports_dataset === 'existing' ? 'not used' : (newAgentData.custom_ui || '')}
                label="Custom UI"
                onChange={e => onChange('custom_ui', e.target.value)}
                disabled={newAgentData.supports_dataset === 'existing'}
              >
                <MenuItem value="internal">Internal</MenuItem>
                <MenuItem value="external">External</MenuItem>
                <MenuItem value="not used">Not Used</MenuItem>
              </Select>
            </FormControl>
            {newAgentData.custom_ui === 'external' && (
              <TextField 
                label="UI URL" 
                value={newAgentData.ui_url || ''} 
                onChange={e => onChange('ui_url', e.target.value)} 
                fullWidth 
                sx={{ mb: 2 }}
                helperText="Optional - will be sent as null if empty"
              />
            )}
            {newAgentData.custom_ui === 'external' && (
              <TextField 
                label="Download URL" 
                value={newAgentData.download_url || ''} 
                onChange={e => onChange('download_url', e.target.value)} 
                fullWidth 
                sx={{ mb: 2 }}
                helperText="Optional - will be sent as null if empty"
              />
            )}
            {/* Download URL field is now conditional above */}
            <TextField label="Demo Video URL" value={newAgentData.demo_video_url || ''} onChange={e => onChange('demo_video_url', e.target.value)} fullWidth sx={{ mb: 2 }} helperText="Optional - will be sent as null if empty" />
            <TextField label="Prompt 1" value={newAgentData.prompt1 || ''} onChange={e => onChange('prompt1', e.target.value)} fullWidth sx={{ mb: 2 }} required error={showValidation && validationErrors.prompt1} helperText={showValidation && validationErrors.prompt1 ? 'Prompt 1 is required and cannot be empty' : ''} />
            <TextField label="Prompt 2" value={newAgentData.prompt2 || ''} onChange={e => onChange('prompt2', e.target.value)} fullWidth sx={{ mb: 2 }} required error={showValidation && validationErrors.prompt2} helperText={showValidation && validationErrors.prompt2 ? 'Prompt 2 is required and cannot be empty' : ''} />
            <TextField label="Prompt 3" value={newAgentData.prompt3 || ''} onChange={e => onChange('prompt3', e.target.value)} fullWidth sx={{ mb: 2 }} required error={showValidation && validationErrors.prompt3} helperText={showValidation && validationErrors.prompt3 ? 'Prompt 3 is required and cannot be empty' : ''} />
            <TextField label="README.md" value={newAgentData.detailed_description || ''} onChange={e => onChange('detailed_description', e.target.value)} fullWidth multiline rows={4} sx={{ mb: 2 }} placeholder="Enter README content in Markdown format..." required error={showValidation && validationErrors.detailed_description} helperText={showValidation && validationErrors.detailed_description ? 'README.md is required and cannot be empty' : ''} />
        </Box>
        </>
        )}
      </DialogContent>
      <DialogActions>
        {!isFileUploadMode && (
          <Button
            onClick={() => {
              setShowValidation(true);
              if (!hasValidationErrors) {
                onSave();
              }
            }}
            variant="contained"
            color="primary"
            disabled={isDeployingAgent || hasValidationErrors}
          >
            {isDeployingAgent ? 'Deploying...' : 'Deploy Agent'}
          </Button>
        )}
        {isFileUploadMode && (
          <Button
            onClick={() => {
              onSave();
            }}
            variant="contained"
            color="primary"
            disabled={isDeployingAgent || !uploadedFile || !fileUploadSuccess}
          >
            {isDeployingAgent ? 'Deploying...' : 'Deploy Agent'}
          </Button>
        )}
        <Button onClick={onCancel} variant="outlined" disabled={isDeployingAgent}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};
export default AgentAddModal;
